import {
    NotificationSubscriptionBaseResponseUnion,
    NotificationType,
} from '@kcftech/prometheus-client';
import { sdClient } from 'contexts/SDKClientContext';
import {
    BaseDataSourceOptions,
    DataSourceQuery,
} from 'custom_hooks/data_hooks/data_source/data_source';
import {
    InfiniteDataSource,
    useInfiniteDataSource,
} from 'custom_hooks/data_hooks/data_source/useInfiniteDataSource';
import {
    PagedDataSource,
    usePagedDataSource,
} from 'custom_hooks/data_hooks/data_source/usePagedDataSource';
import { useMemo } from 'react';
import { buildOrderBy, Filter, toDataSourceResult } from 'utils/query_v3';

export type NotificationSubscriptionsFilter = Readonly<
    {
        corporationId?: string;
        locationId?: string;
        groupId?: string;
        userId?: string;
        nameSearch?: string;
    } & (
        | {
              isSubscriptionGroupMode?: undefined | false;
              notificationTypes?: NotificationType[];
          }
        | {
              isSubscriptionGroupMode: true;
              notificationType: NotificationType;
          }
    )
>;

const getBaseFilter = (
    groupId: string | undefined,
    locationId: string | undefined,
    corporationId: string | undefined,
    isSubscriptionGroupMode: boolean | undefined,
    notificationTypes: NotificationType[],
) => {
    if (groupId) {
        return Filter.eq('GroupId', groupId);
    }
    if (locationId && isSubscriptionGroupMode) {
        if (notificationTypes.some(notificationTypeHasGroupId)) {
            return Filter.and(
                Filter.eq('LocationId', locationId),
                Filter.isNull('Group'),
            );
        }
        return Filter.eq('LocationId', locationId);
    }
    if (locationId) {
        return Filter.eq('LocationId', locationId);
    }
    if (corporationId) {
        return Filter.eq('CorporationId', corporationId);
    }
    return undefined;
};

export const buildFilterExpression = ({
    corporationId,
    locationId,
    groupId,
    userId,
    ...rest
}: NotificationSubscriptionsFilter) => {
    const notificationTypes = rest.isSubscriptionGroupMode
        ? [rest.notificationType]
        : (rest.notificationTypes ?? []);

    return Filter.and(
        getBaseFilter(
            groupId,
            locationId,
            corporationId,
            rest.isSubscriptionGroupMode,
            notificationTypes,
        ),
        userId && Filter.eq('UserId', userId),
        (notificationTypes.length > 0 &&
            Filter.in('NotificationType', notificationTypes)) ||
            undefined,
    );
};

export type NotificationSubscription = NotificationSubscriptionBaseResponseUnion;

export type UseNotificationSubscriptionsDataSourceOptions = BaseDataSourceOptions<
    NotificationSubscription,
    NotificationSubscriptionsFilter
>;

export type NotificationSubscriptionsPagedDataSource = PagedDataSource<
    NotificationSubscription,
    NotificationSubscriptionsFilter
>;

export type NotificationSubscriptionsInfiniteDataSource = InfiniteDataSource<
    NotificationSubscription,
    NotificationSubscriptionsFilter
>;

export const getAllNotificationSubscriptions: DataSourceQuery<
    NotificationSubscription,
    NotificationSubscriptionsFilter
> = ({ filter: { nameSearch, ...filterRest }, sort, page, pageLimit }) => {
    const filter = buildFilterExpression(filterRest);
    const orderBy = buildOrderBy(sort);
    return toDataSourceResult(
        sdClient.getNotificationSubscriptions({
            filter,
            page,
            pageLimit,
            orderBy,
            nameSearch,
        }),
    );
};

export function usePagedNotificationSubscriptionsDataSource({
    ...options
}: UseNotificationSubscriptionsDataSourceOptions): NotificationSubscriptionsPagedDataSource {
    return usePagedDataSource({
        ...options,
        query: getAllNotificationSubscriptions,
    });
}

export function useInfiniteNotificationSubscriptionsDataSource({
    ...options
}: UseNotificationSubscriptionsDataSourceOptions): NotificationSubscriptionsInfiniteDataSource {
    return useInfiniteDataSource({
        ...options,
        query: getAllNotificationSubscriptions,
    });
}

export const notificationTypeHasGroupId = (notificationType: NotificationType): boolean =>
    notificationType === NotificationType.Indicator ||
    notificationType === NotificationType.MachineHealth;

export const getFnGetSingleTypeNotificationSubscriptions: (
    notificationType: NotificationType,
) => DataSourceQuery<
    NotificationSubscription,
    Omit<NotificationSubscriptionsFilter, 'notificationTypes' | 'notificationType'>
> =
    (notificationType) =>
    ({ filter: { nameSearch, ...filterRest }, sort, page, pageLimit }) => {
        const filter = buildFilterExpression({
            ...filterRest,
            ...(filterRest.isSubscriptionGroupMode
                ? {
                      isSubscriptionGroupMode: filterRest.isSubscriptionGroupMode,
                      notificationType,
                  }
                : {
                      isSubscriptionGroupMode: filterRest.isSubscriptionGroupMode,
                      notificationTypes: [notificationType],
                  }),
        });
        const orderBy = buildOrderBy(sort);
        return toDataSourceResult(
            sdClient.getNotificationSubscriptions({
                filter,
                page,
                pageLimit,
                orderBy,
                nameSearch,
            }),
        );
    };

export function usePagedSingleTypeNotificationSubscriptionsDataSource(
    notificationType: NotificationType,
    { ...options }: UseNotificationSubscriptionsDataSourceOptions,
): NotificationSubscriptionsPagedDataSource {
    return usePagedDataSource({
        ...options,
        query: useMemo(
            () => getFnGetSingleTypeNotificationSubscriptions(notificationType),
            [notificationType],
        ),
    });
}
